import { useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import { ReactComponent as ArchiveDispensaryIcon } from 'assets/images/ic-archive-dispensary.svg';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { Container, ContainerItem, Error, Text, Tooltip } from 'ui';

interface Props {
  existingName?: string;
  form: UseFormReturn<FieldValues>;
  handleInvalidDispensaryName?: (value: boolean) => void;
  accountNameField?: string;
}

export const ExistingDispensaries: React.FC<Props> = (props) => {
  const { form, accountNameField } = props;
  const fiDispensaryStore = getFiDispensaryStore();
  const [invalidDispensaryName, setInvalidDispensaryName] = useState(false);
  const [namesList, setNamesList] = useState<{ name: string; archived: boolean }[]>([]);

  useEffect(() => {
    const accountName = form.watch(accountNameField ?? 'accountName')?.trim();
    let list: { name: string; archived: boolean }[] = [];

    if ((accountName ?? '').length < 3) {
      setNamesList([]);
      return;
    } else {
      list = [
        ...fiDispensaryStore.dispensaries
          ?.filter(
            (d) =>
              d?.name?.toLowerCase().includes(accountName?.toLowerCase()) &&
              d?.name?.toLowerCase() !== props.existingName?.toLowerCase()
          )
          .map((disp) => {
            return { name: disp?.name || '', archived: false };
          }),
        ...fiDispensaryStore.bankMuos
          ?.filter(
            (d) =>
              d?.name?.toLowerCase().includes(accountName?.toLowerCase()) &&
              d?.name?.toLowerCase() !== props.existingName?.toLowerCase()
          )
          .map((disp) => {
            return { name: disp?.name || '', archived: false };
          }),
        ...fiDispensaryStore.dispensariesArchived
          .filter(
            (d) =>
              d?.name?.toLowerCase().includes(accountName?.toLowerCase()) &&
              d?.name?.toLowerCase() !== props.existingName?.toLowerCase()
          )
          .map((disp) => {
            return { name: disp?.name || '', archived: true };
          }),
        ...fiDispensaryStore.bankMuosArchived
          ?.filter(
            (d) =>
              d?.name?.toLowerCase().includes(accountName?.toLowerCase()) &&
              d?.name?.toLowerCase() !== props.existingName?.toLowerCase()
          )
          .map((disp) => {
            return { name: disp?.name || '', archived: true };
          })
      ].sort((a, b) => a.name.localeCompare(b.name));

      if (props.existingName) {
        list = list.filter((d) => d.name !== props.existingName);
      }
      setNamesList(list);
    }
    const namesList = list.map((d) => d.name.toLowerCase());
    let isInvalid = false;

    if (namesList.includes(accountName?.toLowerCase())) {
      isInvalid = true;
    } else {
      isInvalid = false;
    }
    setInvalidDispensaryName(isInvalid);
    if (props.handleInvalidDispensaryName) {
      props.handleInvalidDispensaryName(isInvalid);
    }
  }, [form.watch(accountNameField ?? 'accountName')]);

  if ((form.getValues(accountNameField ?? 'accountName') ?? '').length < 3 || !namesList.length) {
    return null;
  }

  return (
    <Container column padding="8px 0 0 0">
      <ContainerItem padding="0">
        <Error content={invalidDispensaryName ? 'An account with this name already exists.' : ''} />
      </ContainerItem>
      <Container padding="0">
        {namesList.length ? (
          <ContainerItem padding="0">
            <Text
              sx={{ fontWeight: '700', fontSize: '14px' }}
              content="Existing accounts with similar names:&nbsp;"
            />
          </ContainerItem>
        ) : null}
        {namesList.map((item, index) => {
          return (
            <ContainerItem padding="0" flex align="center">
              <Text sx={{ fontSize: '14px' }} inline content={item.name} />
              {item.archived ? (
                <Tooltip title="Archived Account" placement="top-start">
                  <ArchiveDispensaryIcon style={{ marginLeft: '6px' }} />
                </Tooltip>
              ) : null}
              {index < namesList.length - 1 ? <Text inline content=",&nbsp;" /> : null}
            </ContainerItem>
          );
        })}
      </Container>
    </Container>
  );
};
